import type { EstimateExclusion, EstimatorInput } from "../types/index.js";

/**
 * Standard planning-level exclusions that apply to any renovation
 * estimate produced by this engine, regardless of project type. These
 * are stated explicitly rather than left implicit, per the Phase 2
 * brief - a planning estimate that doesn't say what it leaves out
 * invites the assumption that it covers everything.
 */
export function buildStandardExclusions(): EstimateExclusion[] {
  return [
    {
      code: "excludes_furniture_decor",
      message: "Excludes furniture, window coverings, and movable decor not itemized as a fixture or appliance selection."
    },
    {
      code: "excludes_unforeseen_conditions",
      message:
        "Excludes costs from unforeseen conditions discovered during demolition (e.g. water damage, mold, outdated wiring/plumbing behind walls or floors)."
    },
    {
      code: "excludes_financing_costs",
      message: "Excludes financing costs, taxes beyond standard sales tax, and any applicable HST/GST rebate calculations."
    },
    {
      code: "excludes_site_specific_logistics",
      message:
        "Excludes site-specific logistics costs (e.g. permit expediting fees, difficult site access, elevator/hoisting for high-rise units) unless explicitly captured in the structural/complexity inputs."
    }
  ];
}

/**
 * Exclusions that depend on what the user did NOT select - the flip
 * side of "do not apply irrelevant adjustments": when a category was
 * left out of the calculation because the input said it wasn't needed,
 * that's stated here too, so it's clear the number reflects a deliberate
 * scoping decision rather than an oversight.
 */
export function buildInputDrivenExclusions(input: EstimatorInput): EstimateExclusion[] {
  const exclusions: EstimateExclusion[] = [];

  if (input.workType.plumbingChanges === "no") {
    exclusions.push({ code: "excludes_plumbing", message: "Excludes plumbing changes - none were requested." });
  }
  if (input.workType.electricalChanges === "no") {
    exclusions.push({ code: "excludes_electrical", message: "Excludes electrical changes - none were requested." });
  }
  if (input.workType.structuralChanges === "no") {
    exclusions.push({ code: "excludes_structural", message: "Excludes structural work - none was requested." });
  }
  if (input.workType.demolitionRequired === "no") {
    exclusions.push({ code: "excludes_demolition", message: "Excludes demolition - none was requested." });
  }
  if (input.accessibility.required === "no") {
    exclusions.push({
      code: "excludes_accessibility",
      message: "Excludes accessibility/mobility accommodations - none were requested."
    });
  }
  if (input.designAndPermits.needsDesignServices === "no" && input.designAndPermits.permitLikelyRequired === "no") {
    exclusions.push({
      code: "excludes_permit_design",
      message: "Excludes a permit/design allowance - neither design services nor a permit were indicated as needed."
    });
  }

  return exclusions;
}
